import { MouseEvent, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Button, ColumnDefinition, Empty, Icon, PageHeading, SortDirection, SortState } from 'barte-design-system';
import { WarningIcon } from '@phosphor-icons/react';
import { usePostHog } from '@posthog/react';

import { InputSearch, TableSkeleton } from '@shared/components';
import { formatDocument, useParamsState } from '@shared/helpers';
import { ROUTES_LEGACY_WEBAPP, TRACK_EVENTS } from '@payfac/constants';
import { useGetSellersList } from '@payfac/services';

import { INITIAL_PARAMS, REQUESTED_FIELDS } from './list.constants';

import styles from './list.module.css';

import type { ISeller } from '@payfac/types';

export const SellersList = () => {
    const { t } = useTranslation();
    const posthog = usePostHog();

    const [params, setParams] = useParamsState(INITIAL_PARAMS);

    const page = Number(params.page) || 1;
    const pageSize = Number(params.pageSize) || 10;
    const { search, sort } = params;

    const [sortKey, sortDirection] = sort?.split(' ') || [];
    const sortState: SortState | undefined = sortDirection
        ? { key: sortKey, direction: sortDirection as SortDirection }
        : undefined;
    const sortApiParam = sortDirection && `${sortKey} ${sortDirection}`;

    const { data, isLoading } = useGetSellersList({
        filters: {
            page,
            size: pageSize,
            fields: REQUESTED_FIELDS,
            ...(search ? { search } : {}),
            ...(sortApiParam ? { sort: sortApiParam } : {}),
        },
    });

    const sellers = data?.data?.content || [];
    const totalPages = data?.data?.totalPages || 1;
    const totalSellers = data?.data?.totalElements ?? 0;

    const handleCompleteRegistration = (event: MouseEvent<HTMLButtonElement>, record: ISeller) => {
        event.stopPropagation();

        posthog.capture(TRACK_EVENTS.SELLERS.COMPLETE_REGISTRATION_CLICKED, {
            seller_id: record.id,
            is_malga_created: record.isSellerMalgaCreated ?? null,
            is_cerc_created: record.isSellerCercCreated ?? null,
        });

        globalThis.location.href = ROUTES_LEGACY_WEBAPP.SELLERS.UPDATE.fullPath(record.id);
    };

    const columns: ColumnDefinition<ISeller>[] = useMemo(() => [
        {
            key: 'companyName',
            title: t('payfac.pageSellersTableColumnName'),
            dataIndex: 'companyName',
            width: '35%',
            sortable: true,
            render: (value) => value ? String(value).toUpperCase() : '-',
        },
        {
            key: 'document',
            title: t('payfac.pageSellersTableColumnDocument'),
            dataIndex: 'document',
            width: '22%',
            sortable: true,
            render: (value) => formatDocument(`${value || ''}`),
        },
        {
            key: 'email',
            title: t('payfac.pageSellersTableColumnEmail'),
            dataIndex: 'email',
            width: '28%',
            render: (value) => value ? String(value).toLowerCase() : '-',
        },
        {
            key: 'registration',
            title: t('payfac.pageSellersTableColumnRegistration'),
            dataIndex: 'isSellerMalgaCreated',
            width: '15%',
            render: (_value, record) => {
                if (record.isSellerMalgaCreated && record.isSellerCercCreated) return null;
                return (
                    <Button
                        variant="outline"
                        size="sm"
                        iconLeft={<Icon icon={WarningIcon} />}
                        onClick={(event) => handleCompleteRegistration(event, record)}
                    >
                        {t('payfac.pageSellersCompleteRegistration')}
                    </Button>
                );
            },
        },
    ], [t]);

    const showEmptyState = !isLoading && sellers?.length === 0;

    const handleSearchChange = (value: string) => {
        setParams({ search: value, page: '1' });
        if (value) {
            posthog.capture(TRACK_EVENTS.SELLERS.LIST_SEARCHED);
        }
    };

    const handlePageChange = (nextPage: number) => {
        setParams({ page: String(nextPage) });
        posthog.capture(TRACK_EVENTS.SELLERS.LIST_PAGE_CHANGED, {
            page: nextPage,
        });
    };

    const handleSort = (key: string, direction: SortDirection) => {
        setParams({
            sort: direction ? `${key} ${direction}` : '',
            page: '1',
        });
        if (direction) {
            posthog.capture(TRACK_EVENTS.SELLERS.LIST_SORTED, {
                key,
                direction,
            });
        }
    };

    const handlePageSizeChange = (nextSize: number) => {
        setParams({ pageSize: String(nextSize), page: '1' });
        posthog.capture(TRACK_EVENTS.SELLERS.LIST_PAGE_SIZE_CHANGED, {
            page_size: nextSize,
        });
    };

    const handleRowClick = (record: ISeller) => {
        posthog.capture(TRACK_EVENTS.SELLERS.ROW_CLICKED);

        globalThis.location.href = ROUTES_LEGACY_WEBAPP.SELLERS.SHOW.fullPath(record.id);
    };

    const handleNewSeller = () => {
        posthog.capture(TRACK_EVENTS.SELLERS.NEW_CLICKED);

        globalThis.location.href = ROUTES_LEGACY_WEBAPP.SELLERS.CREATE.fullPath;
    };

    useEffect(() => {
        posthog.capture(TRACK_EVENTS.SELLERS.LIST_VIEWED);
    }, []);

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{t('payfac.pageSellersTitle')} - Barte</title>
            </Helmet>

            <PageHeading
                title={t('payfac.pageSellersTitle')}
                primaryActionLabel={t('payfac.pageSellersNewButton')}
                onPrimaryAction={handleNewSeller}
            />

            <div className={styles.toolbar}>
                <InputSearch
                    placeholder={t('payfac.pageSellersSearchPlaceholder')}
                    initialValue={search}
                    onDebouncedChange={handleSearchChange}
                    debounceMs={1000}
                />
            </div>

            {showEmptyState && <Empty showButton={false} />}

            {!showEmptyState && (
                <TableSkeleton<ISeller>
                    isLoading={isLoading}
                    columns={columns}
                    data={sellers}
                    rowKey={(record) => record.id}
                    sortState={sortState}
                    onSort={handleSort}
                    pagination={{
                        currentPage: page,
                        totalPages,
                        onPageChange: handlePageChange,
                        pageSize,
                        onPageSizeChange: handlePageSizeChange,
                        totalItems: totalSellers,
                        pageSizeOptions: [10, 25, 50, 100],
                    }}
                    onRowClick={handleRowClick}
                />
            )}
        </div>
    );
};
